import { ORDER_PRIORITY, ORDER_STATUS, ORDER_TYPES } from '../data/orderTypes';

const STORAGE_KEY = 'lockmaster.lockOrders';

const safeParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('Failed to parse lock orders from storage:', error);
    return fallback;
  }
};

const getNowIso = () => new Date().toISOString();

const createId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `order_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const readOrders = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return [];
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return [];
  }

  const parsed = safeParse(stored, []);
  return Array.isArray(parsed) ? parsed : [];
};

const writeOrders = (orders) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
};

const createSeedOrders = (residents = [], locks = []) => {
  const firstResident = residents[0];
  const secondResident = residents[1];
  const firstLock = locks[0];
  const secondLock = locks[1];

  const seedData = [
    {
      id: createId(),
      type: ORDER_TYPES.SETUP,
      status: ORDER_STATUS.NEW,
      priority: ORDER_PRIORITY.NORMAL,
      createdAt: getNowIso(),
      dueDate: null,
      completedAt: null,
      createdBy: 'System',
      assignedTo: '',
      description: 'Ny lås til nyinnflyttet innbygger.',
      notes: '',
      residentId: firstResident?.residentId || '',
      lockVendor: 'Yale',
      lockName: 'Yale Doorman L3',
      location: firstResident?.room || ''
    },
    {
      id: createId(),
      type: ORDER_TYPES.MOVE,
      status: ORDER_STATUS.ASSIGNED,
      priority: ORDER_PRIORITY.HIGH,
      createdAt: getNowIso(),
      dueDate: null,
      completedAt: null,
      createdBy: 'System',
      assignedTo: 'Driftsteam',
      description: 'Flytt lås ved rombytte.',
      notes: '',
      lockId: firstLock?.lockId || '',
      fromResidentId: firstResident?.residentId || '',
      toResidentId: secondResident?.residentId || '',
      newLocation: secondResident?.room || ''
    },
    {
      id: createId(),
      type: ORDER_TYPES.SERVICE,
      status: ORDER_STATUS.IN_PROGRESS,
      priority: ORDER_PRIORITY.URGENT,
      createdAt: getNowIso(),
      dueDate: null,
      completedAt: null,
      createdBy: 'System',
      assignedTo: 'Vaktmester',
      description: 'Lås svarer tregt på kode.',
      notes: '',
      lockId: secondLock?.lockId || '',
      issueCategory: 'Elektronikk',
      reportedBatteryLevel: 'Low'
    },
    {
      id: createId(),
      type: ORDER_TYPES.SERVICE,
      status: ORDER_STATUS.COMPLETED,
      priority: ORDER_PRIORITY.LOW,
      createdAt: getNowIso(),
      dueDate: null,
      completedAt: getNowIso(),
      createdBy: 'System',
      assignedTo: 'Vaktmester',
      description: 'Batteribytte utført.',
      notes: 'Lås testet etter bytte.',
      lockId: firstLock?.lockId || '',
      issueCategory: 'Batteri',
      reportedBatteryLevel: 'Critical'
    }
  ];

  return seedData;
};

export const orderService = {
  initializeDemoData: (residents = [], locks = []) => {
    const currentOrders = readOrders();
    if (currentOrders.length > 0) {
      return currentOrders;
    }

    const seedOrders = createSeedOrders(residents, locks);
    writeOrders(seedOrders);
    return seedOrders;
  },

  getAll: () => {
    const orders = readOrders();
    return [...orders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  },

  getById: (orderId) => {
    const orders = readOrders();
    return orders.find((order) => order.id === orderId) || null;
  },

  create: (orderData) => {
    const orders = readOrders();
    const createdOrder = {
      id: createId(),
      status: ORDER_STATUS.NEW,
      priority: ORDER_PRIORITY.NORMAL,
      createdAt: getNowIso(),
      dueDate: null,
      completedAt: null,
      createdBy: 'System',
      assignedTo: '',
      description: '',
      notes: '',
      ...orderData
    };

    const nextOrders = [createdOrder, ...orders];
    writeOrders(nextOrders);
    return createdOrder;
  },

  update: (orderId, patch) => {
    const orders = readOrders();
    const nextOrders = orders.map((order) => (
      order.id === orderId ? { ...order, ...patch } : order
    ));

    writeOrders(nextOrders);
    return nextOrders.find((order) => order.id === orderId) || null;
  },

  updateStatus: (orderId, status) => {
    const statusPatch = {
      status,
      completedAt: status === ORDER_STATUS.COMPLETED ? getNowIso() : null
    };

    return orderService.update(orderId, statusPatch);
  },

  remove: (orderId) => {
    const orders = readOrders();
    const nextOrders = orders.filter((order) => order.id !== orderId);
    writeOrders(nextOrders);
  }
};

export default orderService;
